import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RepairshopOrderSubType } from '../@core/constants';
import { Filter } from '../models/filter';
import { CreditNotesFilter } from '../models/filters/credit-notes-filter';
import { InvoiceFilter } from '../models/filters/invoice-filter';
import { Invoice } from '../models/invoice';
import { InvoiceWithpayment } from '../models/invoice-without-payment';
import { Paging } from '../models/page';
import { CreditReferenceTransactionRequestModel } from '../models/request/credit-reference-transaction-request-model';
import { PaymentsRequestModel } from '../models/request/PaymentRequestModel';
import { PaymentTransactionRequestModel } from '../models/request/PaymentTransactionRequestModel';
import { ReturnTransactionRequest } from '../models/request/return-transaction-request';
import { ResponseModel } from '../models/response-model';
import { PaymentTransactionResponse } from '../models/response/paymentTransactionResponse';
import { ReturnTransactionResponse } from '../models/response/return-transaction-response';
import { ConfigurationService } from './configuration.service';
import { ParamsService } from './params.service';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {

  private url = `${environment.url}/invoices`;
  private readonly TRUE_STRING = 'true';
  private readonly FALSE_STRING = 'false';
  constructor(private http: HttpClient, private configService: ConfigurationService, private paramService: ParamsService) { }

  proccessTransaction(transaction: Array<PaymentTransactionRequestModel>): Observable<ResponseModel<Array<PaymentTransactionResponse>>> {
    return this.http.post<ResponseModel<Array<PaymentTransactionResponse>>>(`${this.url}/paymentTransaction`, transaction);
  }

  simulatePaymentTransaction(transaction: PaymentTransactionRequestModel) {
    return this.http.post<ResponseModel<PaymentTransactionResponse>>(`${this.url}/simulatePaymentTransaction`, transaction);
  }
  readInvoices(invoiceFilter: Filter<InvoiceFilter>): Observable<ResponseModel<Paging<Array<Invoice>>>> {
    let params = new HttpParams();
    params = params
      .set("page", invoiceFilter.page.toString() || "0")
      .set("pageSize", invoiceFilter.pageSize.toString() || "20");

    params = invoiceFilter.value.salesOrg ? params.set("salesOrg", invoiceFilter.value.salesOrg) : params;
    params = invoiceFilter.value.orderCode ? params.set("orderCode", invoiceFilter.value.orderCode) : params;
    params = invoiceFilter.value.fiscalEstablishment ? params.set("fiscalEstablishment", invoiceFilter.value.fiscalEstablishment) : params;
    params = invoiceFilter.value.fiscalEmisionPoint ? params.set("fiscalEmisionPoint", invoiceFilter.value.fiscalEmisionPoint) : params;
    params = invoiceFilter.value.fiscalDocumentType ? params.set("fiscalDocumentType", invoiceFilter.value.fiscalDocumentType) : params;
    params = invoiceFilter.value.number ? params.set("number", invoiceFilter.value.number) : params;
    params = invoiceFilter.value.branchCode ? params.set("branchCode", invoiceFilter.value.branchCode) : params;
    params = invoiceFilter.value.fullNumber ? params.set("fullNumber", invoiceFilter.value.fullNumber) : params;
    params = invoiceFilter.value.code ? params.set("code", invoiceFilter.value.code) : params;

    return this.http.get<ResponseModel<Paging<Array<Invoice>>>>(`${this.url}`, { params });
  }

  readCreditNotes(filter: Filter<CreditNotesFilter>): Observable<ResponseModel<Paging<Array<Invoice>>>> {

    let params = new HttpParams();
    params = params
      .set("page", filter.page.toString() || "0")
      .set("pageSize", filter.pageSize.toString() || "20")
      .set("salesOrg", this.configService.readSelectedBranchAndSalesOrg().salesOrg.code);

    params = filter.value.branchCode ? params.set("branchCode", filter.value.branchCode) : params;
    params = filter.value.customerCode ? params.set("customerCode", filter.value.customerCode) : params;
    params = filter.value.orderType ? params.set("orderType", filter.value.orderType) : params;
    params = filter.value.sellerName ? params.set("sellerName", filter.value.sellerName) : params;
    params = filter.value.startDate ? params.set("startDate", filter.value.startDate) : params;
    params = filter.value.endDate ? params.set("endDate", filter.value.endDate) : params;
    params = filter.value.fullNumber ? params.set("fullNumber", filter.value.fullNumber) : params;
    params = filter.value.usableAsPayment ? params.set("usableAsPayment", "true") : params;
    params = filter.value.repairshopOrderSubType ? params.set("repairshopOrderSubType", filter.value.repairshopOrderSubType) : params.set('repairshopOrderSubType', RepairshopOrderSubType.UNSPECIFIED);
    params = filter.value.orderCode ? params.set("orderCode", filter.value.orderCode) : params;
    return this.http.get<ResponseModel<Paging<Array<Invoice>>>>(this.url, { params });

  }

  processReturnTransction(transaction: ReturnTransactionRequest) {
    return this.http.post<ResponseModel<ReturnTransactionResponse>>(`${this.url}/returnTransaction`, transaction);
  }

  updateInvoiceRTN(updateRTNInvoiceRequest: { rtn: string, invoiceNumber: string, salesOrg: string }): Observable<ResponseModel<Invoice>> {
    let params = new HttpParams();
    params = updateRTNInvoiceRequest.rtn ? params.set('rtn', updateRTNInvoiceRequest.rtn) : params;
    params = updateRTNInvoiceRequest.salesOrg ? params.set('salesOrg', updateRTNInvoiceRequest.salesOrg) : params;

    return this.http.put<ResponseModel<Invoice>>(`${this.url}/updateRTN/${updateRTNInvoiceRequest.invoiceNumber}`, { params });
  }

  activateCreditNote(paymentRequestModel: PaymentsRequestModel, customerCode: string, fullInvoiceNumber: string) {
    let params = new HttpParams();
    params = params.set("customerCode", customerCode);
    params = params.set("fullInvoiceNumber", fullInvoiceNumber);
    return this.http.post<ResponseModel<Invoice>>(`${this.url}/payments`, paymentRequestModel, { params });
  }

  async readInvoiceWithoutPayments(filter: Filter<any>, salesOrgCode: string, branchCode: string): Promise<ResponseModel<Paging<Array<InvoiceWithpayment>>>> {
    let url = this.url + "/invoicesWithoutPayments";

    let orderTypes = (await this.paramService.readParam("invoiceWithoutPayment-orderTypes").toPromise()).response.value;

    let params = new HttpParams();
    params = params
      .set("branchCode", branchCode)
      .set("salesOrgCode", salesOrgCode)
      .set("orderType", orderTypes)
      .set("page", filter.page.toString() || "0")
      .set("pageSize", filter.pageSize.toString() || "20");

    return this.http.get<ResponseModel<Paging<Array<InvoiceWithpayment>>>>(`${url}`, { params }).toPromise();
  }

  createCreditReferencesTransaction(request: CreditReferenceTransactionRequestModel) {
    return this.http.post<ResponseModel<PaymentTransactionResponse>>(`${this.url}/creditReferencesTransaction`, request);
  }
}